import React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  Handshake as HandshakeIcon,
  Logout as LogoutIcon,
  AccountBalanceWallet as WalletIcon,
  Storefront as StorefrontIcon,
  People as PeopleIcon,
  ReceiptLong as ReceiptLongIcon,
  Sell as SellIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import PartnerLanguageSelector from './PartnerLanguageSelector';
import { partnerTheme, partnerNavActiveBg } from './partnerTheme';
import { formatCredits } from '../../utils/credits';

// Layout del PORTAL PARTNER: tema cálido propio (ThemeProvider anidado),
// navbar comercial con el saldo mayorista siempre a la vista y footer con el
// recordatorio de facturación mensual sin pasarela.

interface PartnerLayoutProps {
  children: React.ReactNode;
}

const PartnerLayout: React.FC<PartnerLayoutProps> = ({ children }) => {
  const { me, reseller, logout } = usePartnerAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/partner/login');
  };

  const navItems = [
    { path: '/partner', label: t('partner.nav.dashboard', 'Inicio'), icon: <StorefrontIcon fontSize="small" /> },
    { path: '/partner/clients', label: t('partner.nav.clients', 'Clientes'), icon: <PeopleIcon fontSize="small" /> },
    // Catálogo con SU lectura (coste, precio de referencia y margen): es su
    // herramienta de venta, así que va en la barra y no escondido en un cliente.
    { path: '/partner/packages', label: t('partner.nav.packages', 'Catálogo'), icon: <SellIcon fontSize="small" /> },
    { path: '/partner/billing', label: t('partner.nav.billing', 'Facturación'), icon: <ReceiptLongIcon fontSize="small" /> },
    // La guía va en la barra, no escondida en el menú de perfil: el portal
    // mueve dinero real (bolsa, techo, márgenes, planes recurrentes) y nada de
    // eso se deduce mirando la pantalla.
    { path: '/partner/ayuda', label: t('partner.nav.help', 'Ayuda'), icon: <HelpOutlineIcon fontSize="small" /> }
  ];

  const isActive = (path: string) =>
    path === '/partner' ? location.pathname === '/partner' : location.pathname.startsWith(path);

  const initial = (me?.user?.full_name || reseller?.name || 'P').charAt(0).toUpperCase();

  return (
    <ThemeProvider theme={partnerTheme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
              {/* Marca del portal partner */}
              <Box
                onClick={() => navigate('/partner')}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mr: 4 }}
              >
                <HandshakeIcon sx={{ color: 'primary.main', fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>
                    WAI Partner
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                    {t('partner.nav.tagline', 'Portal de partners')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexGrow: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    startIcon={item.icon}
                    sx={{
                      color: isActive(item.path) ? 'primary.main' : 'text.primary',
                      fontWeight: isActive(item.path) ? 700 : 500,
                      bgcolor: isActive(item.path) ? partnerNavActiveBg : 'transparent',
                      px: 1.5
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>

              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Saldo mayorista siempre a la vista */}
                {reseller && (
                  <Chip
                    icon={<WalletIcon />}
                    color="primary"
                    variant="outlined"
                    label={t('partner.nav.wholesaleBalance', 'Bolsa: {{balance}} créditos', {
                      balance: formatCredits(reseller.wholesale_balance)
                    })}
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                  />
                )}

                <PartnerLanguageSelector />

                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>{initial}</Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{ sx: { mt: 1.5, minWidth: 220 } }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {reseller?.name || t('partner.nav.partner', 'Partner')}
                    </Typography>
                    {me?.user?.email && (
                      <Typography variant="caption" color="text.secondary">
                        {me.user.email}
                      </Typography>
                    )}
                  </Box>
                  <Divider />
                  {navItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      onClick={() => { navigate(item.path); setAnchorEl(null); }}
                      sx={{ display: { md: 'none' } }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText>{item.label}</ListItemText>
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('auth.logout', 'Cerrar sesión')}</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1 }}>
          {children}
        </Box>

        <Box
          component="footer"
          sx={{ py: 3, mt: 'auto', borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <Container maxWidth="lg">
            <Typography variant="body2" color="text.secondary" align="center">
              {t('partner.footer', 'Portal Partner de WAI · Se factura mensualmente, sin pasarela de pago')}
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PartnerLayout;
